/**
 * Pure exam keydown policy for live Ivori / Pearson taking screens.
 * No React import — QuizDisplaySection maps decisions onto existing handlers.
 */

/**
 * True when exam shortcuts must yield to the focused control.
 * Checkbox / radio are not treated as text-entry (Pearson options + flag).
 *
 * @param {EventTarget | Node | null | undefined} target
 * @returns {boolean}
 */
export function isExamShortcutEditableTarget(target) {
  if (target == null) return false;

  let el = target;
  if (el.nodeType === 3) {
    el = el.parentElement;
  }
  if (el == null || typeof el.closest !== 'function') return false;

  const tag = String(el.tagName || '').toLowerCase();

  if (tag === 'textarea' || tag === 'select') return true;

  if (tag === 'input') {
    const type = String(el.type || el.getAttribute('type') || 'text').toLowerCase();
    return type !== 'checkbox' && type !== 'radio';
  }

  if (el.isContentEditable === true) return true;

  const ce = el.closest('[contenteditable]');
  if (!ce) return false;
  return ce.getAttribute('contenteditable') !== 'false';
}

/**
 * Map a keydown event to a 0-based MCQ option index, or null.
 * Uses event.key '1'..'9' only so NumLock-off Numpad PageDown is not stolen.
 *
 * @param {{ key?: string } | null | undefined} event
 * @returns {number | null}
 */
export function getExamDigitOptionIndex(event) {
  if (!event || typeof event.key !== 'string') return null;
  const { key } = event;
  if (key.length === 1 && key >= '1' && key <= '9') {
    return Number(key) - 1;
  }
  return null;
}

/**
 * @typedef {object} ExamKeyboardSnapshot
 * @property {string} examMode
 * @property {boolean} isSubmitted
 * @property {boolean} hasCurrentQuestion
 * @property {number} currentQuestionIndex
 * @property {number} totalQuestions
 * @property {number} optionCount
 * @property {boolean} isMcq
 * @property {boolean} isEssay
 * @property {boolean} isFreeText
 * @property {boolean} pearsonLiveShowsTextarea
 * @property {boolean} reviewScreenOpen
 * @property {boolean} showExplainModalVisible
 * @property {boolean} showEndExamModalVisible
 * @property {boolean} incompleteWarningVisible
 * @property {boolean} [showEntireScreenModalVisible]
 * @property {boolean} [showNavigatorModalVisible]
 * @property {boolean} [showSolutionModalVisible]
 * @property {boolean} [showEndReviewConfirmVisible]
 * @property {boolean} [showResponseRequiredVisible]
 * @property {boolean} [isTmuaPearsonPlayer]
 * @property {boolean} [lightboxOpen]
 *
 * @typedef {object} ExamKeyboardDecision
 * @property {'ignore'|'prev'|'next'|'selectOption'|'noop'|'endExam'|'endReview'|'confirmYes'|'confirmNo'|'dismissOk'|'instructions'|'openCalculator'|'reviewFlagged'|'reviewIncomplete'|'reviewAll'|'returnToReview'|'highlight'|'strikethrough'} type
 * @property {boolean} [preventDefault]
 * @property {number} [optionIndex]
 */

const IGNORE = { type: 'ignore' };

/**
 * Decide what a live-exam keydown should do. Never submits, flags, or wraps.
 *
 * @param {KeyboardEvent | object} event
 * @param {ExamKeyboardSnapshot} snapshot
 * @returns {ExamKeyboardDecision}
 */
export function resolveExamKeyboardAction(event, snapshot = {}) {
  if (!event) return IGNORE;
  if (event.repeat === true) return IGNORE;
  if (event.isComposing === true) return IGNORE;
  if (event.ctrlKey || event.metaKey || event.altKey) return IGNORE;
  if (event.shiftKey) return IGNORE;
  if (isExamShortcutEditableTarget(event.target)) return IGNORE;
  if (snapshot.isSubmitted) return IGNORE;
  if (snapshot.examMode !== 'ivori' && snapshot.examMode !== 'pearson') return IGNORE;
  if (!snapshot.hasCurrentQuestion || snapshot.totalQuestions < 1) return IGNORE;
  if (snapshot.lightboxOpen) return IGNORE;
  if (
    snapshot.reviewScreenOpen ||
    snapshot.showExplainModalVisible ||
    snapshot.showEndExamModalVisible ||
    snapshot.incompleteWarningVisible ||
    snapshot.showEntireScreenModalVisible ||
    snapshot.showNavigatorModalVisible ||
    snapshot.showSolutionModalVisible ||
    snapshot.showEndReviewConfirmVisible ||
    snapshot.showResponseRequiredVisible
  ) {
    return IGNORE;
  }

  const isArrowLeft = event.key === 'ArrowLeft';
  const isArrowRight = event.key === 'ArrowRight';
  const optionIndex = getExamDigitOptionIndex(event);
  const isArrowKey = isArrowLeft || isArrowRight;

  // These screens have a textarea/free-text control somewhere on them, but
  // that alone shouldn't kill arrow-key prev/next navigation - only digit
  // option-selection is actually incompatible with a writing task. A focused
  // textarea is already handled above via isExamShortcutEditableTarget.
  if ((snapshot.isEssay || snapshot.isFreeText) && !isArrowKey) return IGNORE;
  if (snapshot.examMode === 'pearson' && snapshot.pearsonLiveShowsTextarea && !isArrowKey) return IGNORE;

  if (!isArrowLeft && !isArrowRight && optionIndex === null) return IGNORE;

  if (isArrowLeft) {
    return { type: 'prev', preventDefault: true };
  }

  if (isArrowRight) {
    const isLastItem = snapshot.currentQuestionIndex >= snapshot.totalQuestions - 1;
    if (snapshot.isTmuaPearsonPlayer && isLastItem) {
      return { type: 'next', preventDefault: true };
    }
    if (snapshot.examMode === 'pearson' && isLastItem) {
      return { type: 'noop', preventDefault: true };
    }
    return { type: 'next', preventDefault: true };
  }

  const optionCount = Number(snapshot.optionCount) || 0;
  if (snapshot.isMcq && optionIndex < optionCount) {
    return { type: 'selectOption', optionIndex, preventDefault: true };
  }

  return IGNORE;
}

/**
 * Access keys for the TMUA Pearson-player sitting. Runs even when submitted
 * or overlays are visible. Arrows/digits stay in resolveExamKeyboardAction.
 *
 * @param {KeyboardEvent | object} event
 * @param {object} snapshot
 * @returns {ExamKeyboardDecision}
 */
export function resolveTmuaPearsonAccessKey(event, snapshot = {}) {
  if (!event) return IGNORE;
  if (event.repeat === true) return IGNORE;
  if (event.isComposing === true) return IGNORE;
  if (event.ctrlKey || event.metaKey) return IGNORE;
  if (event.shiftKey) return IGNORE;
  if (!snapshot.isTmuaPearsonPlayer) return IGNORE;
  if (snapshot.lightboxOpen) return IGNORE;

  // UCAT also accepts Alt+letter (REPLICA: Alt+N Next, Alt+F Flag). TMUA/LNAT/ESAT/TARA
  // stay plain-letter only, matching their shipped snapshots (isUcatPearsonPlayer unset).
  const altOk = snapshot.isUcatPearsonPlayer === true;
  if (event.altKey && !altOk) return IGNORE;
  if (event.altKey && snapshot.calculatorActive) return IGNORE;
  if (isExamShortcutEditableTarget(event.target)) return IGNORE;

  const key = String(event.key || '');
  const lower = key.toLowerCase();
  const yesNoOpen = Boolean(
    snapshot.showEndExamModalVisible
    || snapshot.showEndReviewConfirmVisible
    || snapshot.showReadyToBeginVisible
  );
  const okOnlyOpen = Boolean(snapshot.showResponseRequiredVisible || snapshot.showEntireScreenModalVisible);

  if (yesNoOpen) {
    if (lower === 'y') return { type: 'confirmYes', preventDefault: true };
    if (lower === 'n') return { type: 'confirmNo', preventDefault: true };
    return IGNORE;
  }

  if (okOnlyOpen) {
    if (key === 'Enter') return { type: 'dismissOk', preventDefault: true };
    if (lower === 'n') return IGNORE;
    return IGNORE;
  }

  if (snapshot.lnatAnnotateAvailable === true) {
    if (lower === 'j') return { type: 'highlight', preventDefault: true };
    if (lower === 'w') return { type: 'strikethrough', preventDefault: true };
  }

  if (lower === 'u' && snapshot.isSubmitted) {
    return { type: 'instructions', preventDefault: true };
  }

  if (lower === 'f' && snapshot.flagAvailable && !snapshot.calculatorActive) {
    return { type: 'flag', preventDefault: true };
  }

  if (event.altKey && lower === 'p' && snapshot.prevAvailable) {
    return { type: 'prev', preventDefault: true };
  }

  if (event.altKey && lower === 'c' && snapshot.calculatorToggleAvailable) {
    return { type: 'openCalculator', preventDefault: true };
  }

  if (event.altKey && lower === 'v' && snapshot.reviewTabsAvailable) {
    return { type: 'reviewFlagged', preventDefault: true };
  }

  if (event.altKey && lower === 'i' && snapshot.reviewTabsAvailable) {
    return { type: 'reviewIncomplete', preventDefault: true };
  }

  if (event.altKey && lower === 'a' && snapshot.reviewTabsAvailable) {
    return { type: 'reviewAll', preventDefault: true };
  }

  if (event.altKey && lower === 's' && snapshot.returnToReviewAvailable) {
    return { type: 'returnToReview', preventDefault: true };
  }

  if (lower === 'e' && event.altKey && snapshot.endReviewAvailable) {
    return { type: 'endReview', preventDefault: true };
  }

  if (lower === 'e' && snapshot.endExamAvailable) {
    return { type: 'endExam', preventDefault: true };
  }

  if (lower === 'n' && snapshot.nextAvailable && !snapshot.calculatorActive) {
    return { type: 'next', preventDefault: true };
  }

  return IGNORE;
}
